import { isDeliveryAddressConfirmed } from '../Cart/cartCouponUtils';
import { normalizeCartLineAddOns } from '../OurMenu/cartAddOnUtils';
import { validateLocalMobile } from '../../utils/phone';

/**
 * @param {Record<string, string>} errors
 * @returns {boolean}
 */
export function hasCheckoutErrors(errors) {
  return Object.keys(errors).length > 0;
}

/**
 * @param {{
 *   form: {
 *     fullName: string,
 *     phone: string,
 *     email: string,
 *     fulfillmentType: string,
 *     address1: string,
 *     city: string,
 *     pincode: string,
 *   },
 *   selectedAddressId: number | null,
 *   deliveryPricingReady: boolean,
 *   deferDeliveryAndTax: boolean,
 *   chargesLoading: boolean,
 *   cartEmpty: boolean,
 * }} ctx
 * @returns {Record<string, string>}
 */
export function validateCheckoutForPayment(ctx) {
  const errors = {};
  const { form } = ctx;

  if (ctx.cartEmpty) {
    errors._general = 'Your cart is empty.';
    return errors;
  }

  if (ctx.chargesLoading) {
    errors._general = 'Please wait while we calculate your order total.';
    return errors;
  }

  if (ctx.deferDeliveryAndTax) {
    errors._general =
      'Confirm a delivery address with map location, or choose pickup, to see delivery and tax before paying.';
    return errors;
  }

  if (!String(form.fullName || '').trim()) {
    errors.fullName = 'Full name is required.';
  }

  const phoneCheck = validateLocalMobile(form.phone, '91');
  if (!phoneCheck.ok) {
    errors.phone = phoneCheck.message;
  }

  if (form.fulfillmentType === 'delivery') {
    if (ctx.selectedAddressId == null && !isDeliveryAddressConfirmed(form)) {
      errors.addressPick = 'Select a saved delivery address or enter a complete address.';
    } else if (!ctx.deliveryPricingReady) {
      errors.addressPick =
        'Use a saved address from My Addresses (with map location) for delivery pricing.';
    }
  }

  return errors;
}

/**
 * @param {{
 *   form: Record<string, unknown>,
 *   selectedAddressId: number | null,
 *   appliedCoupon: unknown,
 *   cartItems: Array<{ id: number, name: string, price: number, quantity: number, image?: string, category?: string }>,
 *   cartTotal: number,
 *   discount: number,
 *   packagingFee: number,
 *   delivery: number,
 *   gstTax: number,
 *   gstPercent: number,
 *   grandTotal: number,
 *   distanceKm: number | null,
 * }} params
 */
export function buildCheckoutPayload({
  form,
  selectedAddressId,
  appliedCoupon,
  cartItems,
  cartTotal,
  discount,
  packagingFee,
  delivery,
  gstTax,
  gstPercent,
  grandTotal,
  distanceKm,
}) {
  return {
    form: {
      fullName: String(form.fullName || '').trim(),
      phone: String(form.phone || '').trim(),
      email: String(form.email || '').trim(),
      fulfillmentType: form.fulfillmentType === 'pickup' ? 'pickup' : 'delivery',
      address1: String(form.address1 || '').trim(),
      address2: String(form.address2 || '').trim(),
      city: String(form.city || '').trim(),
      state: String(form.state || '').trim(),
      pincode: String(form.pincode || '').trim(),
      notes: String(form.notes || '').trim(),
      deliveryTime: String(form.deliveryTime || 'asap'),
    },
    selectedAddressId: selectedAddressId != null ? Number(selectedAddressId) : null,
    appliedCoupon: appliedCoupon ?? null,
    cartItems: cartItems.map((item) => {
      const addOns = normalizeCartLineAddOns(item.addOns);
      const hostUnitPrice = Number(
        item.hostUnitPrice != null ? item.hostUnitPrice : item.price,
      ) || 0;
      return {
        id: item.id,
        name: item.name,
        price: hostUnitPrice,
        hostUnitPrice,
        quantity: Number(item.quantity),
        image: item.image,
        category: item.category,
        variantLabel: item.variantLabel,
        addOns,
      };
    }),
    charges: {
      cartTotal: Number(cartTotal),
      discount: Number(discount),
      packagingFee: Number(packagingFee),
      delivery: Number(delivery),
      gstTax: Number(gstTax),
      gstPercent: Number(gstPercent),
      grandTotal: Number(grandTotal),
    },
    distanceKm: distanceKm != null && Number.isFinite(Number(distanceKm)) ? Number(distanceKm) : 0,
    savedAt: Date.now(),
  };
}
